/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import { Box, Stack } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useRegisterRefetchPageData } from "../../../hooks/useRegisterRefetchPageData.ts";
import { useCase } from "../../../provider/CaseProvider";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { apiPost } from "../../../services/Api/apiCall";
import { getCaseLink } from "../../../services/Product/ProductService/productService";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { ProductUnion } from "../../../types/ProductUnion";
import ButtonLoading from "../../Button/ButtonLoading";
import caseReferenceFileStyles from "../../Case/CaseReferenceFile/caseReferenceFileStyles";
import ContentBox from "../../ContentBox/ContentBox";
import PageContentContainer from "../../Container/PageContentContainer";
import LegalBirdPageLoading from "../../ContentLoader/LegalBirdPageLoading";
import MediaObjectFolderView from "../../MediaObjectSection/MediaObjectFolderView";
import PageHeadline from "../../PageHeadline/PageHeadline";

type CaseImportDocumentsFromRelatedCasePageProps = {
  headline: string;
  getRelatedCaseId: (product: ProductUnion) => number | null | undefined;
};

const CaseImportDocumentsFromRelatedCasePage = ({
  headline,
  getRelatedCaseId,
}: CaseImportDocumentsFromRelatedCasePageProps) => {
  const { product, isLoading, refreshCase } = useCase();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const currentUser = useCurrentUser();
  const [selectedMediaObjects, setSelectedMediaObjects] = useState<number[]>([]);
  const relatedCaseId = product ? getRelatedCaseId(product) : undefined;
  const mediaObjectsFilterRelatedCase = {
    product: product?.productClassName,
    productId: relatedCaseId,
  };

  const { data: mediaObjects } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilterRelatedCase),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilterRelatedCase),
    enabled: !!product && relatedCaseId !== null && relatedCaseId !== undefined,
  });

  useRegisterRefetchPageData(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.collection("media_objects", mediaObjectsFilterRelatedCase),
    });
    refreshCase();
  });

  if (!product || isLoading) {
    return <LegalBirdPageLoading />;
  }

  if (relatedCaseId === null || relatedCaseId === undefined) {
    return null;
  }

  const handleImportDocuments = async () => {
    let snackbarMessage = "Dokument erfolgreich importiert";
    let snackbarVariant: "custom" | "error" = "custom";

    try {
      await apiPost("/lb/services/copy_media_object_to_related_case", {
        productId: product.id,
        relatedCaseId,
        mediaObjectIds: selectedMediaObjects,
        createdBy: currentUser["@id"],
      });
    } catch (e) {
      snackbarMessage = "Dokument konnte nicht importiert werden";
      snackbarVariant = "error";
    }

    enqueueSnackbar(snackbarMessage, {
      variant: snackbarVariant,
      buttonType: "link",
      buttonLink: getCaseLink(product),
      buttonTarget: "_self",
      buttonText: "Zum Fall",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  };

  return (
    <PageContentContainer>
      <PageHeadline main={headline} />
      <ContentBox>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <Box sx={{ width: "100%" }}>
            <MediaObjectFolderView
              mediaObjects={mediaObjects?.["hydra:member"] ?? []}
              selectableView
              selectedMediaObjects={selectedMediaObjects}
              setSelectedMediaObjects={setSelectedMediaObjects}
              isMediaObjectNonInteractive={true}
            />
          </Box>
          <ButtonLoading
            isLoading={isLoading}
            onClick={handleImportDocuments}
            variant={"outlined"}
            sx={caseReferenceFileStyles.button}
          >
            Kopieren
          </ButtonLoading>
        </Stack>
      </ContentBox>
    </PageContentContainer>
  );
};

export default CaseImportDocumentsFromRelatedCasePage;
